/**
 * 문서 양식 목록(문서 결재 → 문서 양식 → 문서 제출 유형)의 줄 구성과 「증명서 추가」 규칙.
 *
 * 목록은 직원이 실제로 쓰는 양식을 그대로 보여 준다.
 * - 증명서: 조직에 저장된 증명서. 활성 증명서가 하나도 없으면 직원 앱은 기본 증명서를 보여 주므로
 *   (`listCertificateTemplateOptions`) 아직 저장되지 않은 기본 증명서도 「기본 서식 사용 중」 줄로 보인다.
 * - 결재 서식: 9종이 늘 보인다. 저장되지 않은 서식은 결재 문서가 기본 서식으로 그려지므로
 *   (`resolveTemplate`) 「기본 서식 사용 중」 줄로 보이고, 「결재 서식 변경」으로 우리 병원 서식을 만든다.
 */
import { type DocumentTemplatePreset, isCertificateTemplateCode } from "@hereby/core/constants";

export interface StoredDocTemplate {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export type DocTemplateSection = "certificate" | "form";

export type DocTemplateRow<T extends StoredDocTemplate = StoredDocTemplate> =
  | { key: string; section: DocTemplateSection; source: "stored"; template: T }
  | { key: string; section: DocTemplateSection; source: "preset"; preset: DocumentTemplatePreset };

function sectionOf(code: string): DocTemplateSection {
  return isCertificateTemplateCode(code) ? "certificate" : "form";
}

/** 목록 줄 — 증명서 묶음 먼저, 결재 서식 묶음 다음. 저장된 줄의 순서는 서버 순서(sortOrder)를 따른다. */
export function buildDocTemplateRows<T extends StoredDocTemplate>(
  stored: readonly T[],
  presets: readonly DocumentTemplatePreset[],
): DocTemplateRow<T>[] {
  const storedCodes = new Set(stored.map((t) => t.code));
  const presetCodes = new Set(presets.map((p) => p.code));

  const certs: DocTemplateRow<T>[] = stored
    .filter((t) => sectionOf(t.code) === "certificate")
    .map((t) => ({ key: t.id, section: "certificate", source: "stored", template: t }));
  const hasActiveCert = certs.some((r) => r.source === "stored" && r.template.isActive);
  if (!hasActiveCert) {
    for (const p of presets) {
      if (p.kind === "certificate" && !storedCodes.has(p.code)) {
        certs.push({
          key: `preset:${p.code}`,
          section: "certificate",
          source: "preset",
          preset: p,
        });
      }
    }
  }

  const forms: DocTemplateRow<T>[] = [];
  for (const p of presets) {
    if (p.kind !== "form") {
      continue;
    }
    const t = stored.find((s) => s.code === p.code);
    forms.push(
      t
        ? { key: t.id, section: "form", source: "stored", template: t }
        : { key: `preset:${p.code}`, section: "form", source: "preset", preset: p },
    );
  }
  for (const t of stored) {
    if (sectionOf(t.code) === "form" && !presetCodes.has(t.code)) {
      forms.push({ key: t.id, section: "form", source: "stored", template: t });
    }
  }
  return [...certs, ...forms];
}

/** 새 증명서 코드 — 증명서 판정(`_cert` 로 끝남)·50자 제한·조직 내 유일을 지킨다. 사용자에게는 보이지 않는다. */
export function newCertificateCode(
  existingCodes: readonly string[],
  random: () => number = Math.random,
): string {
  const taken = new Set(existingCodes);
  for (;;) {
    const token = Math.floor(random() * 36 ** 6)
      .toString(36)
      .padStart(6, "0");
    const code = `custom_${token}_cert`;
    if (!taken.has(code)) {
      return code;
    }
  }
}

/** 이름 중복(앞뒤 공백 무시). 수정 중인 양식 자신은 뺀다. */
export function hasDuplicateTemplateName(
  name: string,
  stored: readonly { id: string; name: string }[],
  excludeId = "",
): boolean {
  const wanted = name.trim();
  if (!wanted) {
    return false;
  }
  return stored.some((t) => t.id !== excludeId && t.name.trim() === wanted);
}

/** 다른 증명서에서 시작할 때 채워 둘 이름 — 「재직증명서 (복사본)」, 겹치면 「(복사본 2)」… */
export function copyTemplateName(name: string, existingNames: readonly string[]): string {
  const taken = new Set(existingNames.map((n) => n.trim()));
  const base = `${name.trim()} (복사본)`;
  if (!taken.has(base)) {
    return base;
  }
  for (let n = 2; ; n++) {
    const candidate = `${name.trim()} (복사본 ${n})`;
    if (!taken.has(candidate)) {
      return candidate;
    }
  }
}
